import { html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BasePage } from '@vitesse/base-ui/components/base-page';
import { statusBadge } from '@vitesse/base-ui/components/templates';
import { t } from '@vitesse/base-ui/i18n';
import { getSession } from '@vitesse/base-ui';
import { FleetControl } from '@vitesse/proto/vitesse/v1/fleet_control_pb.js';
import type { BackendClient } from '../bridge/backend-client';
import '@vitesse/base-ui/elements/vitesse-editable-description';
import type { GatewayEntry } from '../managers/gateway-entry';
import type { Controller, ConnectionStatus } from '../../core/domain/controller';
import { connectionsOf, type ModelView } from '../../core/domain/model';
import type { Integration } from '../../core/domain/integration';
import { upstreamNodeId } from './vitesse-flow-canvas';
import '@vitesse/base-ui/elements/vitesse-icon';
import './vitesse-flow-canvas';

/*
 * <vitesse-canvas> — the ORG-WIDE Flows page (top of the tree). It shows the
 * whole org's data-flow graph: every gateway's PLC controllers (sources) and
 * models (transforms), wired by the models' real bindings. Multi-gateway is
 * "iterate the entries" (gateway-entry.ts): we aggregate `entry.controllers.all`
 * + `entry.models.all` across ALL gateways and hand flat arrays to the graph.
 *
 * BasePage chrome (title · description · status · AI hints). The graph +
 * connector engine live in <vitesse-flow-canvas>. Cardinal Rule: this page
 * reads manager state + listens for 'change', never fetches. A model node click
 * bubbles 'open-model'; we resolve which gateway it lives on and navigate.
 */
@customElement('vitesse-canvas')
export class VitesseCanvas extends BasePage {
  @property({ attribute: false }) entries: GatewayEntry[] = [];
  @property({ attribute: false }) backend?: BackendClient;
  /** The org's Flows-page note, fetched once on open + re-fetched after an edit. */
  @state() private orgDescription = '';
  private orgDescLoaded = false;
  @state() private controllers: Controller[] = [];
  @state() private models: ModelView[] = [];
  /** aggregated only for the org-wide status badges (the graph doesn't draw them yet). */
  @state() private integrations: Integration[] = [];
  /** model id → its gateway fingerprint, for navigating on node click. */
  private modelGateway = new Map<string, string>();
  private subscribed: GatewayEntry[] = [];

  private readonly onChange = (): void => this.aggregate();

  private aggregate(): void {
    const controllers: Controller[] = [];
    const models: ModelView[] = [];
    const integrations: Integration[] = [];
    const mg = new Map<string, string>();
    for (const e of this.entries) {
      const gw = e.gateway.deviceFingerprint;
      for (const c of e.controllers.all) controllers.push(c);
      for (const m of e.models.all) {
        models.push(m);
        mg.set(m.id, gw);
      }
      for (const i of e.integrations.all) integrations.push(i);
    }
    this.modelGateway = mg;
    this.controllers = controllers;
    this.models = models;
    this.integrations = integrations;
  }

  /** (Re)subscribe to every entry's controller + model managers so the graph
   *  re-aggregates on any gateway's change. */
  private resubscribe(): void {
    for (const e of this.subscribed) {
      e.controllers.removeEventListener('change', this.onChange);
      e.models.removeEventListener('change', this.onChange);
      e.integrations.removeEventListener('change', this.onChange);
    }
    this.subscribed = [...this.entries];
    for (const e of this.subscribed) {
      e.controllers.addEventListener('change', this.onChange);
      e.models.addEventListener('change', this.onChange);
      e.integrations.addEventListener('change', this.onChange);
    }
    this.aggregate();
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.resubscribe();
    void this.loadOrgDescription();
  }

  private async loadOrgDescription(): Promise<void> {
    if (this.orgDescLoaded || !this.backend) return;
    this.orgDescLoaded = true;
    try {
      this.orgDescription = await this.backend.api.getOrgDescription();
    } catch {
      /* leave the placeholder — the note is optional page metadata */
    }
  }

  private commitOrgDescription(next: string): void {
    if (!this.backend) return;
    // Optimistic: the store is unary (no stream), so reflect the edit locally and
    // let the write settle; a failure leaves the last good value on the next open.
    this.orgDescription = next;
    /*
     * A SWALLOWED FAILURE IS WHY THIS WENT UNNOTICED FOR SO LONG.
     *
     * `.catch(() => {})` discarded every rejection, and one of them was real: the
     * RPC was missing from the api-gateway's proxy allow-list, so over the cloud
     * transport it 404'd on every write. The optimistic update meant the text
     * stayed on screen, so the edit looked saved right up until the next reload.
     *
     * The optimism is still right — the store is unary, and blocking the caret on
     * a round-trip would be worse. What was wrong is being silent about losing.
     * Logged rather than thrown: this runs from an editor commit with no error
     * surface to raise into, and a console line is the difference between a bug
     * somebody can find and one they cannot.
     */
    void this.backend.api.putOrgDescription(next).catch((e: unknown) => {
      console.warn('the organization description was not saved', e);
    });
  }

  protected renderDescription() {
    // The Flows page's own note — org-scoped, editable in place. The fixed
    // explainer becomes the placeholder, so an un-noted org still reads as one.
    const canEdit = !!this.backend && getSession()?.allowsRpc(FleetControl.method.putOrgDescription) !== false;
    return html`<vitesse-editable-description
      .value=${this.orgDescription}
      placeholder=${t('canvas.description')}
      hint=${t('dashboards.descriptionEditHint')}
      .canEdit=${canEdit}
      .commit=${(next: string) => this.commitOrgDescription(next)}
    ></vitesse-editable-description>`;
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    for (const e of this.subscribed) {
      e.controllers.removeEventListener('change', this.onChange);
      e.models.removeEventListener('change', this.onChange);
      e.integrations.removeEventListener('change', this.onChange);
    }
    this.subscribed = [];
  }
  protected willUpdate(changed: PropertyValues): void {
    if (changed.has('entries')) this.resubscribe();
  }

  private openModel(modelId: string): void {
    const gatewayId = this.modelGateway.get(modelId);
    if (!gatewayId) return;
    this.dispatchEvent(
      new CustomEvent('navigate', { detail: { view: 'model', gatewayId, modelId }, bubbles: true, composed: true }),
    );
  }

  protected pageTitle(): string {
    return t('canvas.title');
  }
  protected titleIcon(): string {
    return 'waypoints';
  }
  protected renderBadges() {
    const connOnline = (s: ConnectionStatus): boolean => s === 'connected';
    // "offline" = down/faulted (not transitional connecting/discovered)
    const connOffline = (s: ConnectionStatus): boolean => s === 'disconnected' || s === 'error' || s === 'reconnecting';

    // Connections = live source links (connected PLCs + integrations).
    const connections =
      this.controllers.filter((c) => connOnline(c.status)).length +
      this.integrations.filter((i) => connOnline(i.status)).length;

    // Active paths = live flow edges (a running model × each resolvable upstream its
    // wiring reads), deduped — the SAME resolution the graph uses to draw its glowing wires.
    const known = new Set<string>();
    for (const c of this.controllers) known.add(`src-${c.id}`);
    for (const m of this.models) known.add(`xf-${m.id}`);
    const paths = new Set<string>();
    for (const m of this.models) {
      if (m.status !== 'running') continue;
      const to = `xf-${m.id}`;
      for (const p of Object.values(connectionsOf(m))) {
        const from = upstreamNodeId(p);
        if (from && from !== to && known.has(from)) paths.add(`${from}~${to}`);
      }
    }

    // Offline = anything not doing its job: down PLCs + integrations, stopped/faulted models.
    const offline =
      this.controllers.filter((c) => connOffline(c.status)).length +
      this.integrations.filter((i) => connOffline(i.status)).length +
      this.models.filter((m) => m.status === 'stopped' || m.status === 'error' || m.status === 'unknown').length;

    return html`${statusBadge('green', t('canvas.connections', { count: connections }))}${statusBadge(
      'blue',
      t('canvas.activePaths', { count: paths.size }),
    )}${offline > 0 ? statusBadge('yellow', t('canvas.offline', { count: offline })) : nothing}`;
  }
  protected aiHints(): string[] {
    return [t('canvas.hintCreate'), t('canvas.hintPrograms')];
  }
  protected onBack(): void {
    this.dispatchEvent(new CustomEvent('navigate', { detail: { view: 'home' }, bubbles: true, composed: true }));
  }
  protected backLabel(): string {
    return t('canvas.back');
  }
  protected renderContent() {
    return html`<vitesse-flow-canvas
      .controllers=${this.controllers}
      .models=${this.models}
      @open-model=${(e: CustomEvent<{ modelId: string }>) => this.openModel(e.detail.modelId)}
    ></vitesse-flow-canvas>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-canvas': VitesseCanvas;
  }
}
